
export class LocationServiceNotAvailable extends Error {}

interface WebAppInterface {
  showAlert(message: string): Promise<void>

  showConfirm(message: string): Promise<boolean>

  expand(): void

  openTelegramLink(url: string): void

  openLink(url: string, onOpen?: () => unknown): void

  getStartParam(): string | null

  close(): void

  ready(): void

  initData(): string

  openInvoice(invoiceUrl: string): Promise<'pending' | 'failed' | 'cancelled' | 'paid'>

  changeBackButtonVisibility(show: boolean): void

  setBackButtonCallback(callback: () => unknown): void

  hapticFeedbackImpact(style?: 'light' | 'medium' | 'heavy'): void

  hapticFeedbackNotification(type: 'error' | 'success' | 'warning'): void

  hapticFeedbackSelectionChanged(): void

  addToHomeScreen(): void

  requestLocation(): Promise<{ latitude: number, longitude: number } | null>

  shareToStory(mediaUrl: string, params?: {
    text?: string, widget_link?:
      { url: string, name?: string }
  }): void

  requestFullscreen(): void

  isNotMobile: boolean
  isPremium: boolean
  language: string

  safeAreaInset: {
    top: number
    bottom: number
    left: number
    right: number
  }

  contentSafeAreaInset: {
    top: number
    bottom: number
    left: number
    right: number
  }
}

const WebApp = Telegram.WebApp
class NonTelegramWebapp implements WebAppInterface {
  public async showAlert(message: string) {
    alert(message)
  }

  public openInvoice(invoiceUrl: string): Promise<'pending' | 'failed' | 'cancelled' | 'paid'> {
    window.open(invoiceUrl, '_blank')
    return Promise.resolve('paid')
  }

  public addToHomeScreen() {
    return
  }

  public async requestLocation(){
    return null
  }

  public shareToStory(url: string, params?: {
    text?: string, widget_link?:
      { url: string, name?: string }
  }): void {
    alert(`Shared story with url ${url}`)
    return
  }

  public expand(): void {
    return
  }

  public async showConfirm(message: string): Promise<boolean> {
    return new Promise(resolve => {
      resolve(confirm(message))
    })
  }

  public close(): void {
    window.close()
  }

  public ready(): void {
    return
  }

  public changeBackButtonVisibility(): void {
    return
  }

  public setBackButtonCallback(): void {
    return
  }

  public hapticFeedbackImpact(): void {
    return
  }

  public hapticFeedbackNotification(): void {
    return
  }

  public hapticFeedbackSelectionChanged(): void {
    return
  }

  public get language(): string {
    return new URLSearchParams(window.location.search).get('lang') ?? 'en'
  }

  public get isPremium(): boolean {
    return new URLSearchParams(window.location.search).get('isPremium') === 'true'
  }

  public openTelegramLink(url: string): void {
    window.open(url, '_blank')
  }

  public openLink(url: string): void {
    window.open(url, '_blank')
  }

  public getStartParam(): string | null {
    return new URL(window.location.href).searchParams.get('tgWebAppStartParam') ?? null
  }

  public initData(): string {
    return new URL(window.location.href).searchParams.get('initData') ?? ''
  }

  public requestFullscreen() {
    return
  }

  public isNotMobile = false

  public safeAreaInset = {
    top: 20,
    bottom: 40,
    left: 0,
    right: 0,
  }
  public contentSafeAreaInset = {
    top: 20,
    bottom: 20,
    left: 0,
    right: 0,
  }
}

class TelegramWebApp implements WebAppInterface {
  public showAlert(message: string): Promise<void> {
    return new Promise(resolve => {
      WebApp.showAlert(message, () => resolve())
    })
  }

  public openInvoice(invoiceUrl: string): Promise<'pending' | 'failed' | 'cancelled' | 'paid'> {
    return new Promise((resolve) => {
      console.log('promise of openInvoice', invoiceUrl)
      WebApp.openInvoice(invoiceUrl, resolve)
    })
  }

  public addToHomeScreen() {
    WebApp.addToHomeScreen()
  }

  public async showConfirm(message: string): Promise<boolean> {
    return new Promise(resolve => {
      WebApp.showConfirm(message, (confirmed) => resolve(confirmed))
    })
  }

  public get isPremium() {
    return WebApp.initDataUnsafe.user?.is_premium ?? false
  }

  public expand(): void {
    WebApp.expand()
  }

  public close(): void {
    WebApp.close()
  }

  public shareToStory(mediaUrl: string, params?: {
    text?: string, widget_link?:
      { url: string, name?: string }
  }): void {
    WebApp.shareToStory(mediaUrl, params)
  }

  public ready(): void {
    WebApp.disableVerticalSwipes()
    WebApp.setHeaderColor('#080808')
    WebApp.ready()
  }

  public get isNotMobile(): boolean {
    return !['ios', 'android', 'android_x', 'unigram'].includes(WebApp.platform)
  }

  public changeBackButtonVisibility(show: boolean): void {
    if (show) {
      WebApp.BackButton.show()
    } else {
      WebApp.BackButton.hide()
    }
  }

  public setBackButtonCallback(callback: () => unknown) {
    WebApp.BackButton.onClick(callback)
  }

  public get language(): string {
    return WebApp.initDataUnsafe.user?.language_code ?? 'en'
  }

  private initLocation() {
    if (!WebApp.LocationManager.isLocationAvailable) throw new LocationServiceNotAvailable()
    if (WebApp.LocationManager.isInited) return Promise.resolve()
    return new Promise((resolve) => {
      WebApp.LocationManager.init(resolve as () => void)
    })
  }

  private requestLocationFromTg(): Promise<{ latitude: number, longitude: number }> {
    return new Promise((resolve) => {
      WebApp.LocationManager.getLocation((result) => {
        if (!result) throw new Error('User rejected location request')
        resolve({ latitude: result.latitude, longitude: result.longitude })
      })
    })
  }

  public async requestLocation(): Promise<{ latitude: number, longitude: number } | null> {
    try {
      await this.initLocation()
    } catch (e) {
      if (e instanceof LocationServiceNotAvailable) return null
      throw e
    }
    if (!WebApp.LocationManager.isAccessRequested) {
      WebApp.LocationManager.openSettings()
      throw new Error('Pending settings')
    }
    if (!WebApp.LocationManager.isAccessGranted) {
      WebApp.showAlert('You have to grant location access to do this task.')
      WebApp.LocationManager.openSettings()
      throw new Error('Location access denied')
    }
    return await this.requestLocationFromTg()
  }

  public hapticFeedbackImpact(style: 'light' | 'medium' | 'heavy' = 'heavy'): void {
    WebApp.HapticFeedback.impactOccurred(style)
  }

  public hapticFeedbackNotification(type: 'error' | 'success' | 'warning'): void {
    WebApp.HapticFeedback.notificationOccurred(type)
  }

  public hapticFeedbackSelectionChanged(): void {
    WebApp.HapticFeedback.selectionChanged()
  }

  public openTelegramLink(url: string): void {
    WebApp.openTelegramLink(url)
  }

  public openLink(url: string): void {
    WebApp.openLink(url)
  }

  public getStartParam(): string | null {
    return WebApp.initDataUnsafe.start_param ?? null
  }

  public initData(): string {
    return WebApp.initData
  }

  public get safeAreaInset() {
    return WebApp.safeAreaInset
  }

  public get contentSafeAreaInset() {
    return WebApp.contentSafeAreaInset
  }

  public requestFullscreen() {
    WebApp.requestFullscreen()
  }
}

export default (WebApp.initData ? new TelegramWebApp() : new NonTelegramWebapp()) as WebAppInterface
